/**
 * Staff-door Cognito token exchange (public client + PKCE) + session keys for #5 shell.
 *
 * Fold: D6W6 exchange + allowlist, mirrored into STAFF_SESSION_KEYS so app-shell
 * hasStaffSession() works after SSO. Tokens never written to the DOM / never logged.
 */

import { previewRedirectUri } from './authorize';
import { STAFF_DOOR } from './endpoints';

export const STAFF_STORAGE_PREFIX = 'dubbed.staff.';

export const STAFF_SESSION_KEYS = {
  verifier: 'dubbed.staff.pkce.verifier',
  state: 'dubbed.staff.pkce.state',
  accessToken: 'dubbed.staff.token.access',
  idToken: 'dubbed.staff.token.id',
  refreshToken: 'dubbed.staff.token.refresh',
  afterLogout: 'dubbed.staff.afterLogout',
  /** Compact session blob (D6W6 lineage) for claims reload. */
  session: 'dubbed.staff.session',
} as const;

/** Aliases used by door views (same strings as STAFF_SESSION_KEYS). */
export const VERIFIER_KEY = STAFF_SESSION_KEYS.verifier;
export const STATE_KEY = STAFF_SESSION_KEYS.state;
export const SESSION_KEY = STAFF_SESSION_KEYS.session;

export type PrincipalKind = 'machine' | 'staff';

/** Founders-first email allowlist for the staff door (SPA gate). Cognito Lambda gate follows. */
export const STAFF_EMAIL_ALLOWLIST = [
  'nicholas@lumifymd.com',
  'nikki@lumifymd.com',
  'elice@lumifymd.com',
] as const;

export interface StaffSessionPublic {
  readonly kind: PrincipalKind;
  readonly sub: string;
  readonly clientId: string;
  readonly email: string | null;
  readonly tokenUse: string | null;
  readonly scopeCount: number;
  readonly hasStaffSession: boolean;
  readonly expiresAt: string | null;
}

interface TokenResponse {
  readonly access_token?: string;
  readonly id_token?: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
  readonly token_type?: string;
}

interface StoredSession {
  readonly accessToken: string;
  readonly idToken: string | null;
  readonly refreshToken: string | null;
  readonly obtainedAt: number;
  readonly expiresIn: number | null;
}

function base64UrlToJson(segment: string): Record<string, unknown> {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const json = atob(padded + pad);
  return JSON.parse(json) as Record<string, unknown>;
}

/** Decode JWT payload only (display/classify). Signature is not verified in the SPA. */
export function decodeJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split('.');
  if (parts.length < 2 || !parts[1]) {
    throw new Error('Token is not a JWT');
  }
  return base64UrlToJson(parts[1]);
}

export function classifyPrincipalKind(clientId: string, subject: string): PrincipalKind {
  return subject.length > 0 && subject !== clientId ? 'staff' : 'machine';
}

function parseScopes(claim: unknown): string[] {
  if (typeof claim === 'string') {
    return claim.split(' ').filter((scope) => scope.length > 0);
  }
  if (Array.isArray(claim)) {
    return claim.filter((scope): scope is string => typeof scope === 'string');
  }
  return [];
}

export function publicClaimsFromAccessToken(
  accessToken: string,
  idToken: string | null = null,
): StaffSessionPublic {
  const payload = decodeJwtPayload(accessToken);
  const sub = typeof payload.sub === 'string' ? payload.sub : '';
  const clientId =
    typeof payload.client_id === 'string'
      ? payload.client_id
      : typeof payload.clientId === 'string'
        ? payload.clientId
        : STAFF_DOOR.clientId;
  const scopes = parseScopes(payload.scope ?? payload.scp);
  const exp = typeof payload.exp === 'number' ? payload.exp : null;

  let email: string | null = null;
  if (idToken) {
    try {
      const idPayload = decodeJwtPayload(idToken);
      if (typeof idPayload.email === 'string' && idPayload.email.trim()) {
        email = idPayload.email.trim().toLowerCase();
      }
    } catch {
      email = null;
    }
  }
  if (!email && typeof payload.email === 'string' && payload.email.trim()) {
    email = payload.email.trim().toLowerCase();
  }

  return {
    kind: classifyPrincipalKind(clientId, sub),
    sub,
    clientId,
    email,
    tokenUse: typeof payload.token_use === 'string' ? payload.token_use : null,
    scopeCount: scopes.length,
    hasStaffSession: scopes.includes('dubbed-emr/staff.session'),
    expiresAt: exp !== null ? new Date(exp * 1000).toISOString() : null,
  };
}

export function isStaffEmailAllowed(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return (STAFF_EMAIL_ALLOWLIST as readonly string[]).includes(normalized);
}

function mirrorFlatTokens(stored: StoredSession): void {
  sessionStorage.setItem(STAFF_SESSION_KEYS.accessToken, stored.accessToken);
  if (stored.idToken) {
    sessionStorage.setItem(STAFF_SESSION_KEYS.idToken, stored.idToken);
  } else {
    sessionStorage.removeItem(STAFF_SESSION_KEYS.idToken);
  }
  if (stored.refreshToken) {
    sessionStorage.setItem(STAFF_SESSION_KEYS.refreshToken, stored.refreshToken);
  } else {
    sessionStorage.removeItem(STAFF_SESSION_KEYS.refreshToken);
  }
}

export async function exchangeAuthorizationCode(params: {
  readonly code: string;
  readonly verifier: string;
  readonly redirectUri?: string;
}): Promise<StaffSessionPublic> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: STAFF_DOOR.clientId,
    code: params.code,
    redirect_uri: params.redirectUri ?? previewRedirectUri(),
    code_verifier: params.verifier,
  });

  const response = await fetch(STAFF_DOOR.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed (HTTP ${response.status})`);
  }

  const tokens = (await response.json()) as TokenResponse;
  if (!tokens.access_token || typeof tokens.access_token !== 'string') {
    throw new Error('Token exchange returned no access token');
  }

  const stored: StoredSession = {
    accessToken: tokens.access_token,
    idToken: typeof tokens.id_token === 'string' ? tokens.id_token : null,
    refreshToken: typeof tokens.refresh_token === 'string' ? tokens.refresh_token : null,
    obtainedAt: Date.now(),
    expiresIn: typeof tokens.expires_in === 'number' ? tokens.expires_in : null,
  };
  const claims = publicClaimsFromAccessToken(tokens.access_token, stored.idToken);
  if (!isStaffEmailAllowed(claims.email)) {
    clearStaffSession();
    throw new Error(
      'This Google account is not on the staff allowlist. Ask Nick to add your @lumifymd.com address.',
    );
  }

  sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
  mirrorFlatTokens(stored);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  return claims;
}

export function loadStaffSessionPublic(): StaffSessionPublic | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) {
    const access = sessionStorage.getItem(STAFF_SESSION_KEYS.accessToken);
    if (!access) return null;
    try {
      const claims = publicClaimsFromAccessToken(
        access,
        sessionStorage.getItem(STAFF_SESSION_KEYS.idToken),
      );
      if (!isStaffEmailAllowed(claims.email)) {
        clearStaffSession();
        return null;
      }
      return claims;
    } catch {
      return null;
    }
  }
  try {
    const stored = JSON.parse(raw) as StoredSession;
    if (!stored.accessToken || typeof stored.accessToken !== 'string') return null;
    const claims = publicClaimsFromAccessToken(
      stored.accessToken,
      typeof stored.idToken === 'string' ? stored.idToken : null,
    );
    if (!isStaffEmailAllowed(claims.email)) {
      clearStaffSession();
      return null;
    }
    return claims;
  } catch {
    return null;
  }
}

export function clearStaffKeys(storage: Storage): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(STAFF_STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}

/** Clears staff sessionStorage on this origin only. Does not touch Cognito cookies. */
export function clearStaffSession(): void {
  clearStaffKeys(sessionStorage);
}

export function markStaffSignedOut(): void {
  sessionStorage.setItem(STAFF_SESSION_KEYS.afterLogout, '1');
}

export function consumeStaffSignedOut(): boolean {
  const marked = sessionStorage.getItem(STAFF_SESSION_KEYS.afterLogout) === '1';
  sessionStorage.removeItem(STAFF_SESSION_KEYS.afterLogout);
  return marked;
}

export function hasStaffSession(): boolean {
  return Boolean(sessionStorage.getItem(STAFF_SESSION_KEYS.accessToken));
}

/** Strip ?code=&state= from the address bar without reloading (code must not linger). */
export function scrubOAuthQueryFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('code') && !url.searchParams.has('state') && !url.searchParams.has('error')) {
    return;
  }
  url.search = '';
  window.history.replaceState({}, document.title, url.pathname + url.hash);
}
